import styles from './checkout.module.css';

import Dinero, { Currency } from 'dinero.js';
import { Cart, CartItem, RentalCartItem, SaleCartItem } from '../../clients/customer-api/cart/cart';
import {
  CouponLineItem,
  DamageWaiverLineItem,
  DeliveryLineItem,
  ExcludeProductFeeRequest,
  FeeLineItem,
  FinancialSummary,
  LineItem,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  SecurityDepositLineItem,
  TaxLineItem,
} from '../../clients/quipli-service/financials/financials';
import { Card } from '../../components/card/card';
import { Divider } from '../../components/divider/divider';
import { Typography, TypographyWeight } from '../../components/typography/typography';
import { Location } from '../../clients/quipli-service/location/location';
import { FieldCounter } from '../../components/field/field';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { Checkbox } from '../../components/checkbox/checkbox';
import { Label } from '../../clients/quipli-service/label/label';
import { Fragment, ReactNode, useState } from 'react';
import { OrderType } from '../../clients/quipli-service/order/order';
import { displayHumanDuration } from '../../util/util';
import classNames from 'classnames';
import Icon from '../../components/icons/icon';
import { Button } from '../../components/form/button';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { ecommerceToast } from '../../util/toasts';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { useCustomer } from '../../hooks/use-customer-context';
import { ProductCustomService, ProductOption } from '../../clients/customer-api/product/customer-product';
import { useCustomerYorentClient } from '../../clients/customer-api/customer-yorent-api-client';
import { formatPhoneNumber } from '../../util/util';
import { ProductTrackingType } from '../../clients/quipli-service/product/product';
import { TaxBreakdown } from '../../clients/quipli-service/tax/tax';

export const CheckoutSummary = ({
  cart,
  summary,
  location,
  customizations,
  config,
  labels,
  excludeFeeIds,
  excludeProductFees,
  excludeDamageWaiver,
  setExcludedFeeIds,
  setExcludedProductFees,
  setExcludedDamageWaiver,
  reload,
  isSubmitting,
  isQuote,
}: {
  cart: Cart;
  summary: FinancialSummary;
  location: Location;
  customizations?: Map<number, { options: ProductOption[]; services: ProductCustomService[] }>;
  config: Configuration[];
  labels: Label[];
  excludeFeeIds?: number[];
  excludeProductFees?: ExcludeProductFeeRequest[];
  excludeDamageWaiver?: boolean;
  setExcludedFeeIds?: (ids: number[]) => void;
  setExcludedProductFees?: (fees: ExcludeProductFeeRequest[]) => void;
  setExcludedDamageWaiver?: (exclude: boolean) => void;
  reload: () => void;
  isSubmitting: boolean;
  isQuote?: boolean;
}) => {
  const { updateCartItem, removeItemFromCart, updateCartMeta } = useCustomerAPI();
  const { refreshCartProductCount } = useCustomerYorentClient();
  const customer = useCustomer();

  const [modal, setModal] = useState<ReactNode>();

  const orderDelivery = summary?.lineItems.find((line) => line.type === 'Delivery') as DeliveryLineItem;
  const delivery = orderDelivery
    ? orderDelivery.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductDelivery' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      ) || Dinero({ amount: 0, currency: location.currency as Currency });
  const orderPickup = summary?.lineItems.find((line) => line.type === 'Pickup') as PickupLineItem;
  const pickup = orderPickup
    ? orderPickup.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductPickup' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      ) || Dinero({ amount: 0, currency: location.currency as Currency });
  const damageWaiver = summary?.lineItems.find((line) => line.type === 'DamageWaiver') as
    | DamageWaiverLineItem
    | undefined;
  const coupon = summary?.lineItems.find((line) => line.type === 'TagPricing' || line.type === 'Coupon') as
    | CouponLineItem
    | undefined;
  const tax = summary?.lineItems.find((line) => line.type === 'Tax') as TaxLineItem;
  const taxInclusive = config.find((c) => c.variable === 'CONF_SHOW_PRICES_WITH_TAX')?.value === '1';
  const taxLabel = config.find((c) => c.variable === 'CONF_PRICE_TAX_LABEL')?.value || 'Tax';
  const locationFees = summary?.lineItems.filter((line) => line.type === 'Fee') as FeeLineItem[];
  const productFees = summary?.lineItems.reduce(
    (acc, line) => (line.type === 'ProductFee' && line.isSelected ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency: location.currency as Currency }),
  );
  const additionalDiscount = summary?.lineItems.find((line) => line.type === 'AdditionalDiscount')?.amount;

  const orderSecurityDeposit = summary?.lineItems.find((line) => line.type === 'SecurityDeposit') as
    | SecurityDepositLineItem
    | undefined;
  const securityDepositPrice = orderSecurityDeposit
    ? orderSecurityDeposit.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductSecurityDeposit' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency: location.currency as Currency }),
      );
  const checkoutText = labels.find((label) => label.key === 'L_CHECKOUT_TEXT')?.caption || '';
  const hidePrices = config.find((c) => c.variable === 'CONF_USER_UNAPPROVED_HIDE_PRICES')?.value === '1' && !customer;
  const displayTaxByJurisdiction =
    config.find((c) => c.variable === 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN')?.value === '1';

  const taxBreakdown = tax?.taxBreakdown || [];
  const zero = Dinero({ amount: 0, currency: location.currency as Currency });
  const deliveryTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'Delivery')?.tax ?? zero);
  }, zero);
  const pickupTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'Pickup')?.tax ?? zero);
  }, zero);
  const damageWaiverTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'DamageWaiver')?.tax ?? zero);
  }, zero);
  const additionalDiscountTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'AdditionalDiscount')?.tax ?? zero);
  }, zero);
  const couponTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'Coupon')?.tax ?? zero);
  }, zero);
  const productFeesTax = taxBreakdown.reduce((total, jurisdiction) => {
    return total.add(jurisdiction.itemizedBreakdown?.find((_) => _.type == 'ProductFee')?.tax ?? zero);
  }, zero);

  const getDuration = (rentalItem: RentalCartItem) => {
    return rentalItem.endDate ? displayHumanDuration(rentalItem.endDate.diff(rentalItem.startDate)) : undefined;
  };
  //const deliveryTax = tax.taxBreakdown?.map(_ => _.itemizedBreakdown.find(_ => _.type === 'Product'))

  const displayPriceLabel = (
    item: CartItem,
    taxInclusive: boolean,
    lineItem?: LineItem,
    taxBreakdown?: TaxBreakdown[],
  ) => {
    console.log(taxBreakdown);
    console.log(item);
    const productTax =
      taxBreakdown?.reduce((total, jurisdiction) => {
        return total.add(
          jurisdiction.itemizedBreakdown?.find(
            (_) => _.type === 'Product' && (_.contractLineItem as ProductLineItem)?.id == item.id,
          )?.tax || Dinero({ amount: 0, currency: location.currency as Currency }),
        );
      }, Dinero({ amount: 0, currency: location.currency as Currency })) ??
      Dinero({ amount: 0, currency: location.currency as Currency });
    const taxMessage = productTax.isZero() ? ` no ${taxLabel} incl.` : ` incl. ${taxLabel}`;
    switch (item.type) {
      case OrderType.Rental:
        return item.product.hidePrices
          ? 'Contact for pricing'
          : taxInclusive
          ? `${lineItem?.amount.add(productTax).toFormat() ?? '-'} ${taxMessage}`
          : lineItem?.amount.toFormat();
      case OrderType.Sale:
        return item.product.hideSalePrices
          ? 'Contact for pricing'
          : taxInclusive
          ? `${lineItem?.amount.add(productTax).toFormat() ?? '-'} ${taxMessage}`
          : lineItem?.amount.toFormat();
    }
  };

  return (
    <Card className="rounded-lg shadow-sm flex flex-col px-0">
      <div className="py-4 px-3 flex flex-col gap-5">
        <Typography type="text-md" weight="semi-bold">
          Summary
        </Typography>

        {cart.items
          .sort((a, b) => (a.id > b.id ? 1 : b.id > a.id ? -1 : 0))
          .map((item) => {
            const lineItem = summary.lineItems.find((li) => li.type === 'Product' && li.id === item.id);
            const fees = summary.lineItems.filter(
              (li) => li.type === 'ProductFee' && li.productLineItemId === item.id,
            ) as ProductFeeLineItem[] | undefined;

            return (
              <div key={`product-${item.id}`} className={styles.cartItemWrapper}>
                <div className={styles.cartItemInfo}>
                  <Typography type="text-xs" weight="semi-bold">
                    {item.product.name || lineItem?.description}
                  </Typography>

                  {item.type === OrderType.Rental && (item as RentalCartItem).endDate && (
                    <Typography type="text-xs" className="text-quipli-black-700">
                      Duration: {getDuration(item as RentalCartItem)}
                    </Typography>
                  )}

                  {/* Options */}
                  {Object.entries(item.options).map(([optionId, valueId]) => {
                    const option = customizations?.get(item.product.id)?.options.find((o) => o.id === +optionId);
                    const name = option?.name;
                    const value = option?.values?.find((v) => v.id === +valueId)?.name;

                    return (
                      name &&
                      value && (
                        <Fragment key={`product-${item.id}-option-${name}`}>
                          <Typography type="text-xs" className="text-quipli-black-700">
                            {name}: {value}
                          </Typography>
                        </Fragment>
                      )
                    );
                  })}

                  {/* Cargo */}
                  {item instanceof SaleCartItem && (
                    <div className="flex flex-col gap-1">
                      <Typography type="text-xs" weight="semi-bold" className="text-quipli-black-500">
                        Cargo Requirements:
                      </Typography>

                      <ul>
                        {item.services.map(({ id, value }) => {
                          const service = customizations?.get(item.product.id)?.services.find((s) => s.id === id);
                          const options = service?.options;

                          return (
                            <li key={`product-${item.id}-service-${id}`}>
                              <div className="flex gap-1 ml-2">
                                <Typography type="text-xs" className="text-quipli-black-700">
                                  - {service?.name}
                                  {options ? ':' : ''}
                                </Typography>
                                <Typography
                                  type="text-xs"
                                  weight={options ? 'regular' : 'semi-bold'}
                                  className={classNames('text-quipli-black-700', {
                                    '!italic': !!options,
                                  })}
                                >
                                  {options ? value : `(${value})`}
                                </Typography>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Product fees */}
                  {fees?.map((fee) => (
                    <div key={`product-${item.id}-fee-${fee.productFeeId}`} className="flex">
                      {fee.optional && (
                        <Checkbox
                          type="ecommercePrimary"
                          isChecked={
                            !excludeProductFees?.find(
                              (pf) => pf.productFeeId === fee.productFeeId && pf.productLineItemId === item.id,
                            )
                          }
                          isDisabled={isSubmitting}
                          onChange={(isChecked) => {
                            if (isChecked) {
                              setExcludedProductFees?.(
                                excludeProductFees?.filter(
                                  (pf) => pf.productFeeId !== fee.productFeeId || pf.productLineItemId !== item.id,
                                ) || [],
                              );
                              updateCartMeta(
                                {
                                  ...cart.meta,
                                  excludeProductFees: excludeProductFees?.filter(
                                    (pf) => pf.productFeeId !== fee.productFeeId || pf.productLineItemId !== item.id,
                                  ),
                                },
                                location.id,
                              );
                            } else {
                              setExcludedProductFees?.([
                                ...(excludeProductFees || []),
                                {
                                  productFeeId: fee.productFeeId,
                                  productLineItemId: item.id,
                                  productId: item.product.id,
                                },
                              ]);
                              updateCartMeta(
                                {
                                  ...cart.meta,
                                  excludeProductFees: [
                                    ...(excludeProductFees || []),
                                    {
                                      productFeeId: fee.productFeeId,
                                      productLineItemId: item.id,
                                      productId: item.product.id,
                                    },
                                  ],
                                },
                                location.id,
                              );
                            }
                          }}
                        />
                      )}

                      <div className="flex gap-1">
                        <Typography type="text-xs" weight="semi-bold" className="text-quipli-black-500">
                          {fee.description}:
                        </Typography>
                        <Typography type="text-xs" className="text-quipli-black-500">
                          {`${fee.amount.toFormat()}${fee.optional ? ' (optional)' : ''}`}
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.cartItemQty}>
                  <Icon icon="Close" size="small" stroke="var(--quipli-black-800)" />

                  <FieldCounter
                    hideButtons
                    value={item.quantity}
                    min={1}
                    disabled={isSubmitting || isQuote}
                    onChange={async (newQty) => {
                      if (isQuote) return;

                      try {
                        await updateCartItem(
                          {
                            ...item,
                            quantity: newQty,
                          },
                          item.product && item.product.trackingType === ProductTrackingType.NonInventory
                            ? true
                            : undefined,
                          location.id,
                        );
                        await refreshCartProductCount();

                        ecommerceToast('Item quantity updated');
                        reload();
                      } catch (e: any) {
                        ecommerceToast(e.message || 'Error updating item quantity', 'error');
                        reload();
                      }
                    }}
                  />
                </div>

                <div
                  className={classNames(styles.cartItemPriceAction, {
                    '!justify-end': isQuote,
                  })}
                >
                  <Typography type="text-sm">
                    {displayPriceLabel(item, taxInclusive, lineItem, taxBreakdown)}
                  </Typography>

                  {!isQuote && (
                    <Button
                      className="icon-button !p-0 !m-0"
                      onClick={() =>
                        !isSubmitting &&
                        setModal(
                          <ConfirmModal
                            prompt="Remove item?"
                            additionalText="This cannot be undone"
                            noLabel="Cancel"
                            yesLabel="Remove"
                            yesButtonYellow
                            isOpen
                            useEcommerceTheme
                            headerIcon={
                              <div className="flex justify-center">
                                <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                              </div>
                            }
                            onConfirm={async () => {
                              await removeItemFromCart(item.id, location.id);
                              await refreshCartProductCount();
                              setModal(undefined);
                              reload();
                            }}
                            close={() => setModal(undefined)}
                          />,
                        )
                      }
                    >
                      <Icon icon="Trash" stroke="var(--grey-400)" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

        <div className="flex flex-col gap-2">
          {locationFees
            .filter((fee) => fee.optional)
            .map((fee, idx) => (
              <div key={`location-fee-${idx}`} className="flex gap-1">
                <Checkbox
                  type="ecommercePrimary"
                  isChecked={!excludeFeeIds?.includes(fee.feeId)}
                  label={`${fee.description} (optional)`}
                  isDisabled={isSubmitting}
                  onChange={(isChecked) => {
                    if (!isChecked) {
                      setExcludedFeeIds?.([...(excludeFeeIds || []), fee.feeId]);
                      updateCartMeta(
                        { ...cart.meta, excludeFeeIds: [...(excludeFeeIds || []), fee.feeId] },
                        location.id,
                      );
                    } else {
                      setExcludedFeeIds?.(excludeFeeIds?.filter((id) => id !== fee.feeId) || []);
                      updateCartMeta(
                        { ...cart.meta, excludeFeeIds: excludeFeeIds?.filter((id) => id !== fee.feeId) },
                        location.id,
                      );
                    }
                  }}
                />
              </div>
            ))}
          {damageWaiver?.optional && (
            <div className="flex gap-1">
              <Checkbox
                type="ecommercePrimary"
                isChecked={!excludeDamageWaiver}
                label={`${damageWaiver.description} (optional)`}
                isDisabled={isSubmitting}
                onChange={(isChecked) => {
                  setExcludedDamageWaiver?.(!isChecked);
                  updateCartMeta({ ...cart.meta, excludeDamageWaiver: !isChecked }, location.id);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <Divider />

      <div className="py-4 flex flex-col gap-2">
        <CheckoutLineItem
          label="Delivery"
          amount={taxInclusive ? delivery.add(deliveryTax) : delivery}
          taxInclusive={taxInclusive}
          taxLabel={taxLabel}
          tax={deliveryTax}
        />

        {pickup && !pickup.isZero() && (
          <CheckoutLineItem
            label="Pickup"
            amount={taxInclusive ? pickup.add(pickupTax) : pickup}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={pickupTax}
          />
        )}

        {coupon && (
          <CheckoutLineItem
            label={`Coupon (${coupon.type === 'TagPricing' ? `Tag{${coupon.code}}` : coupon.code})`}
            amount={taxInclusive ? coupon.amount.add(couponTax) : coupon.amount}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={couponTax}
          />
        )}

        {displayTaxByJurisdiction &&
          tax &&
          tax.taxBreakdown?.map((b) => (
            <CheckoutLineItem label={`${b.jurisdiction} - (${b.percentage}%)`} amount={b.amount} />
          ))}

        {!displayTaxByJurisdiction && tax && <CheckoutLineItem label="Tax" amount={tax.amount} />}

        {damageWaiver && damageWaiver.isSelected && (
          <CheckoutLineItem
            label="Damage Waiver"
            amount={taxInclusive ? damageWaiver.amount.add(damageWaiverTax) : damageWaiver.amount}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={damageWaiverTax}
          />
        )}

        {locationFees
          .filter((fee) => !(fee.optional && !fee.isSelected))
          .map((fee, idx) => (
            <Fragment key={`location-fee-${idx}`}>
              <CheckoutLineItem
                label={fee.description}
                amount={fee.amount.add(
                  taxInclusive
                    ? taxBreakdown.reduce((tax, breakdown) => {
                        return tax.add(
                          breakdown.itemizedBreakdown?.find(
                            (b) => b.type === 'Fee' && (b.contractLineItem as FeeLineItem)?.feeId === fee.feeId,
                          )?.tax ?? Dinero({ amount: 0, currency: location.currency as Currency }),
                        );
                      }, Dinero({ amount: 0, currency: location.currency as Currency }))
                    : Dinero({ amount: 0, currency: location.currency as Currency }),
                )}
                taxInclusive={taxInclusive}
                taxLabel={taxLabel}
                tax={taxBreakdown.reduce((tax, breakdown) => {
                  return tax.add(
                    breakdown.itemizedBreakdown?.find(
                      (b) => b.type === 'Fee' && (b.contractLineItem as FeeLineItem)?.feeId === fee.feeId,
                    )?.tax ?? Dinero({ amount: 0, currency: location.currency as Currency }),
                  );
                }, Dinero({ amount: 0, currency: location.currency as Currency }))}
              />
            </Fragment>
          ))}

        {productFees.getAmount() > 0 && (
          <CheckoutLineItem
            label="Product Fees Total"
            amount={taxInclusive ? productFees.add(productFeesTax) : productFees}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={productFeesTax}
          />
        )}

        {additionalDiscount && (
          <CheckoutLineItem
            label="Additional Discount"
            amount={taxInclusive ? additionalDiscount.add(additionalDiscountTax) : additionalDiscount}
            taxInclusive={taxInclusive}
            taxLabel={taxLabel}
            tax={additionalDiscountTax}
          />
        )}

        <div className="m-1 py-2 rounded-md bg-grey-100">
          <CheckoutLineItem
            label="Total"
            amount={!hidePrices ? summary.total : undefined}
            noAmountText="Sign in to see total."
            weigth="bold"
            className="px-2"
          />
        </div>

        {(securityDepositPrice?.getAmount() || 0) > 0 && (
          <div className="flex justify-between px-3">
            <Typography
              type="text-sm"
              weight="semi-bold"
              className="underline text-quipli-black-500"
            >{`Refundable Security Deposit (Hold)${
              orderSecurityDeposit?.percentage ? ` ${orderSecurityDeposit.percentage}%` : ''
            }`}</Typography>
            <Typography type="text-sm" weight="semi-bold">
              {!hidePrices ? securityDepositPrice.toFormat() : '-'}
            </Typography>
          </div>
        )}

        {checkoutText && (
          <div className="mt-2 px-3">
            <Typography type="text-sm" className="text-quipli-black-500">
              <div dangerouslySetInnerHTML={{ __html: checkoutText }} />
            </Typography>
          </div>
        )}

        {!location.allowDeliverySelection && (
          <div className="mt-2 px-3">
            <Divider />

            <div className="flex items-center gap-2 pt-4">
              <Icon icon="Location01" />
              <div className="flex flex-col">
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-500">
                  {labels.find((label) => label.key === 'M_Pickup_at')?.caption || 'Pickup at'}:
                </Typography>

                <Typography type="text-sm" className="text-quipli-black-500">
                  {`${location.name}, ${location.address.address1}${
                    location.address.address2 ? `, ${location.address.address2}` : ''
                  }${location.address.city ? `, ${location.address.city}` : ''}${
                    location.address.state ? `, ${location.address.state}` : ''
                  }${location.address.zip ? `, ${location.address.zip}` : ''}${
                    location.phone ? `. ${formatPhoneNumber(location.phone)}` : ''
                  }`}
                </Typography>
              </div>
            </div>
          </div>
        )}
      </div>

      {modal}
    </Card>
  );
};

const CheckoutLineItem = ({
  label,
  amount,
  noAmountText = '-',
  taxInclusive = false,
  taxLabel,
  tax,
  weigth = 'regular',
  className = 'px-3',
}: {
  label: string;
  amount?: Dinero.Dinero;
  noAmountText?: string;
  taxInclusive?: boolean;
  taxLabel?: string;
  tax?: Dinero.Dinero;
  weigth?: keyof typeof TypographyWeight;
  className?: string;
}) => (
  <div className={classNames('flex justify-between', className)}>
    <Typography type="text-sm" weight={weigth} className="text-quipli-black-500">
      {label}
    </Typography>
    <Typography type="text-sm" weight={weigth}>
      {amount
        ? amount.toFormat() +
          `${
            taxInclusive
              ? tax && !tax.isZero()
                ? ` incl. ${taxLabel ?? 'Tax'}`
                : ` no ${taxLabel ?? 'Tax'} incl.`
              : ''
          }`
        : noAmountText}
    </Typography>
  </div>
);
